import { Building, Check, ChevronDown } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SalonItemContent } from "./salon-item-content";
import type { SalonSelectorProps } from "./types";
import { useSalonSelector } from "./use-salon-selector";

export function SalonSelector({
  currentSalon,
  availableSalons,
  onSalonChange,
  onTriggerClick,
  placeholder,
  variant = "combobox",
  className = "",
  height = "default",
}: SalonSelectorProps) {
  const { t } = useTranslation("common");

  const {
    open,
    setOpen,
    searchTerm,
    setSearchTerm,
    salonGroups,
    filteredGroups,
    isGroupedByFranchise,
    getHeightClasses,
    handleSalonSelect,
    getCurrentSalonDisplay,
  } = useSalonSelector({ availableSalons, currentSalon, height });

  if (variant === "select") {
    return (
      <Select value={currentSalon?.id || ""} onValueChange={onSalonChange}>
        <SelectTrigger className={`w-full ${getHeightClasses()} ${className}`}>
          <Building className="w-4 h-4 mr-2 text-muted-foreground" />
          <SelectValue placeholder={placeholder || t("sidebar.selectCompany")} />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {isGroupedByFranchise
            ? salonGroups.map((group, groupIndex) => (
                <React.Fragment key={group.franchise?.id || "no-franchise"}>
                  {group.franchise && group.salons.length > 1 && (
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 flex items-center gap-2">
                      {/* <Building className="w-3 h-3" /> */}
                      {group.franchise.name}
                    </div>
                  )}
                  {group.salons.map((salon) => (
                    <SelectItem
                      key={salon.id}
                      value={salon.id}
                      className={
                        group.franchise && group.salons.length > 1
                          ? "pl-6 py-3 h-auto"
                          : "py-3 h-auto"
                      }
                    >
                      <div className="flex items-start justify-between w-full">
                        <SalonItemContent
                          salon={salon}
                          group={group}
                          showFranchiseName={!(group.franchise && group.salons.length > 1)}
                        />
                      </div>
                    </SelectItem>
                  ))}
                  {groupIndex < salonGroups.length - 1 &&
                    group.franchise &&
                    group.salons.length > 1 && <SelectSeparator />}
                </React.Fragment>
              ))
            : salonGroups.flatMap((group) =>
                group.salons.map((salon) => (
                  <SelectItem key={salon.id} value={salon.id} className="py-3 h-auto">
                    <div className="flex items-start justify-between w-full">
                      <SalonItemContent salon={salon} group={group} />
                    </div>
                  </SelectItem>
                ))
              )}
        </SelectContent>
      </Select>
    );
  }

  // Combobox variant with search
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          onClick={(e) => {
            if (onTriggerClick) {
              e.preventDefault();
              onTriggerClick();
            }
          }}
          className={`w-full justify-between ${getHeightClasses()} ${className}`}
        >
          <div className="flex items-center min-w-0 flex-1">
            <Building className="w-4 h-4 mr-2 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
              {currentSalon ? (
                (() => {
                  const displayData = getCurrentSalonDisplay();
                  if (!displayData) return null;

                  if (displayData.isMultiLine) {
                    return (
                      <div className="flex flex-col text-left min-w-0">
                        <div className="font-medium text-sm leading-tight whitespace-normal break-words">
                          {displayData.franchiseName}
                        </div>
                        <div className="text-xs text-muted-foreground leading-tight whitespace-normal break-words">
                          {displayData.salonName}
                          {displayData.location && ` • ${displayData.location}`}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="text-left leading-tight whitespace-normal break-words">
                      {displayData.text}
                    </div>
                  );
                })()
              ) : (
                <span className="text-muted-foreground">
                  {placeholder || t("sidebar.selectCompany")}
                </span>
              )}
            </div>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" side="bottom" align="start">
        <Command>
          <CommandInput
            placeholder={t("search.placeholder", "Search salons...")}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{t("search.noResults", "No salons found.")}</CommandEmpty>

            {isGroupedByFranchise ? (
              filteredGroups.map((group) => (
                <React.Fragment key={group.franchise?.id || "no-franchise"}>
                  {group.franchise && group.salons.length > 1 ? (
                    <CommandGroup
                      heading={
                        <div className="flex items-center gap-2">
                          <Building className="w-3 h-3" />
                          {group.franchise.name}
                        </div>
                      }
                    >
                      {group.salons.map((salon) => (
                        <React.Fragment key={salon.id}>
                          <CommandItem
                            value={`${salon.id}-${salon.name}-${salon.city || ""}-${salon.state || ""}-${group.franchise?.name || ""}`}
                            onSelect={() => handleSalonSelect(salon.id, onSalonChange)}
                            className="pl-6 py-3"
                          >
                            <div className="flex items-center justify-between w-full">
                              <SalonItemContent
                                salon={salon}
                                group={group}
                                showFranchiseName={false}
                              />
                              {currentSalon?.id === salon.id && (
                                <Check className="h-4 w-4 text-primary ml-2" />
                              )}
                            </div>
                          </CommandItem>
                          <CommandSeparator className="mx-3" />
                        </React.Fragment>
                      ))}
                    </CommandGroup>
                  ) : (
                    <CommandGroup>
                      {group.salons.map((salon) => (
                        <React.Fragment key={salon.id}>
                          <CommandItem
                            value={`${salon.id}-${salon.name}-${salon.city || ""}-${group.franchise?.name || ""}`}
                            onSelect={() => handleSalonSelect(salon.id, onSalonChange)}
                            className="py-3"
                          >
                            <div className="flex items-start justify-between w-full">
                              <SalonItemContent salon={salon} group={group} />
                              {currentSalon?.id === salon.id && (
                                <Check className="h-4 w-4 text-primary ml-2 mt-0.5" />
                              )}
                            </div>
                          </CommandItem>
                          <CommandSeparator className="mx-3" />
                        </React.Fragment>
                      ))}
                    </CommandGroup>
                  )}
                </React.Fragment>
              ))
            ) : (
              <CommandGroup>
                {filteredGroups.flatMap((group) =>
                  group.salons.map((salon) => (
                    <React.Fragment key={salon.id}>
                      <CommandItem
                        value={`${salon.id}-${salon.name}-${salon.city || ""}-${salon.state || ""}`}
                        onSelect={() => handleSalonSelect(salon.id, onSalonChange)}
                        className="py-3"
                      >
                        <div className="flex items-start justify-between w-full">
                          <SalonItemContent salon={salon} group={group} />
                          {currentSalon?.id === salon.id && (
                            <Check className="h-4 w-4 text-primary ml-2 mt-0.5" />
                          )}
                        </div>
                      </CommandItem>
                      <CommandSeparator className="mx-3" />
                    </React.Fragment>
                  ))
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
